import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { format } from 'date-fns';
import types from '../../mutation-types';
import ContactAPI from '../../../api/contacts';
import AccountActionsAPI from '../../../api/accountActions';
import AnalyticsHelper from '../../../helper/AnalyticsHelper';
import { CONTACTS_EVENTS } from '../../../helper/AnalyticsHelper/events';

const buildContactFormData = contactParams => {
  const formData = new FormData();
  const { additional_attributes = {}, ...contactProperties } = contactParams;
  Object.keys(contactProperties).forEach(key => {
    if (contactProperties[key]) {
      formData.append(key, contactProperties[key]);
    }
  });
  const { social_profiles, ...additionalAttributesProperties } =
    additional_attributes;
  Object.keys(additionalAttributesProperties).forEach(key => {
    let value = additionalAttributesProperties[key];
    if (value instanceof Date) {
      value = value.toISOString();
    }
    formData.append(`additional_attributes[${key}]`, value);
  });
  Object.keys(social_profiles).forEach(key => {
    formData.append(
      `additional_attributes[social_profiles][${key}]`,
      social_profiles[key]
    );
  });
  return formData;
};

export const raiseContactCreateErrors = error => {
  if (error.response?.status === 422) {
    throw new DuplicateContactException(error.response.data.attributes);
  } else if (error.response?.data?.message) {
    throw new ExceptionWithMessage(error.response.data.message);
  } else {
    throw new Error(error);
  }
};

let latestListRequest = 0;

const startListRequest = commit => {
  latestListRequest += 1;
  commit(types.SET_CONTACT_UI_FLAG, { isFetching: true });
  return latestListRequest;
};

const commitContactsPage = (commit, requestId, { payload, meta }) => {
  if (requestId !== latestListRequest) return false;
  commit(types.CLEAR_CONTACTS);
  commit(types.SET_CONTACTS, payload);
  commit(types.SET_CONTACT_META, meta);
  commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
  return true;
};

const failContactsPage = (commit, requestId) => {
  if (requestId === latestListRequest) {
    commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
  }
};

export const actions = {
  search: async ({ commit }, { search, page, sortAttr, label, perPage }) => {
    const requestId = startListRequest(commit);
    try {
      const { data } = await ContactAPI.search(
        search,
        page,
        sortAttr,
        label,
        '',
        perPage
      );
      commitContactsPage(commit, requestId, data);
    } catch (error) {
      failContactsPage(commit, requestId);
    }
  },

  get: async ({ commit }, { page = 1, sortAttr, label, perPage } = {}) => {
    const requestId = startListRequest(commit);
    try {
      const { data } = await ContactAPI.get(page, sortAttr, label, perPage);
      commitContactsPage(commit, requestId, data);
    } catch (error) {
      failContactsPage(commit, requestId);
    }
  },

  show: async ({ commit }, { id }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await ContactAPI.show(id);
      commit(types.SET_CONTACT_RECORDS, [response.data.payload]);
      commit(types.SET_CONTACT_UI_FLAG, {
        isFetchingItem: false,
      });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, {
        isFetchingItem: false,
      });
    }
  },

  privateMessages: async ({ commit }, { id }) => {
    try {
      const response = await ContactAPI.privateMessages(id);
      commit(types.SET_CONTACT_PRIVATE_MESSAGES, response.data.payload);
    } catch (error) {
      console.log(error);
    }
  },

  privateAttachments: async ({ commit }, { id }) => {
    try {
      const response = await ContactAPI.privateAttachments(id);
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },

  fetchFiles: async ({ commit }, { id }) => {
    try {
      const response = await ContactAPI.fetchFiles(id);
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },

  update: async ({ commit }, { id, isFormData = false, ...contactParams }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      const response = await ContactAPI.update(
        id,
        isFormData ? buildContactFormData(contactParams) : contactParams
      );
      commit(types.EDIT_CONTACT, response.data.payload);
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
      if (error.response?.status === 422) {
        throw new DuplicateContactException(error.response.data.attributes);
      } else {
        throw new Error(error);
      }
    }
  },

  addFile: async ({ commit }, { id, isFormData = false, ...contactParams }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      const response = await ContactAPI.addFile(
        id,
        isFormData ? buildContactFormData(contactParams) : contactParams
      );
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
      return response.data;
    } catch (error) {
      // d99d keep the server's reason (it answers with a plain string or { error })
      const data = error?.response?.data;
      throw new Error(
        (typeof data === 'string' ? data : data?.error) || error.message
      );
    } finally {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    }
  },

  removeFile: async ({ commit }, { id, fileId }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      const response = await ContactAPI.removeFile(id, fileId);
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    }
  },

  updateFile: async (_, { id, fileId, comment }) => {
    const response = await ContactAPI.updateFile(id, fileId, comment);
    return response.data;
  },

  create: async ({ commit }, { isFormData = false, ...contactParams }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isCreating: true });
    try {
      const response = await ContactAPI.create(
        isFormData ? buildContactFormData(contactParams) : contactParams
      );

      AnalyticsHelper.track(CONTACTS_EVENTS.CREATE_CONTACT);
      commit(types.SET_CONTACT_RECORDS, [response.data.payload.contact]);
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      return response.data.payload.contact;
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      return raiseContactCreateErrors(error);
    }
  },

  import: async ({ commit }, file) => {
    commit(types.SET_CONTACT_UI_FLAG, { isCreating: true });
    try {
      await ContactAPI.importContacts(file);
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data.message);
      }
    }
  },

  statusOfLastImport: async ({ commit }, inboxId) => {
    const { data } = await ContactAPI.getImportContactsStatus();
    return data;
  },
  export: async ({ commit }) => {
    try {
      await ContactAPI.exportContacts();
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(error);
      }
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_CONTACT_UI_FLAG, { isDeleting: true });
    try {
      await ContactAPI.delete(id);
      commit(types.SET_CONTACT_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isDeleting: false });
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(error);
      }
    }
  },

  deleteCustomAttributes: async ({ commit }, { id, customAttributes }) => {
    try {
      const response = await ContactAPI.destroyCustomAttributes(
        id,
        customAttributes
      );
      commit(types.EDIT_CONTACT, response.data.payload);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteAvatar: async ({ commit }, id) => {
    try {
      const response = await ContactAPI.destroyAvatar(id);
      commit(types.EDIT_CONTACT, response.data.payload);
    } catch (error) {
      throw new Error(error);
    }
  },

  fetchContactableInbox: async ({ commit }, id) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetchingInboxes: true });
    try {
      const response = await ContactAPI.getContactableInboxes(id);
      const contact = {
        id,
        contactableInboxes: response.data.payload,
      };
      commit(types.SET_CONTACT_RECORDS, [contact]);
    } catch (error) {
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data.message);
      } else {
        throw new Error(error);
      }
    } finally {
      commit(types.SET_CONTACT_UI_FLAG, { isFetchingInboxes: false });
    }
  },

  updatePresence: ({ commit }, data) => {
    commit(types.UPDATE_CONTACTS_PRESENCE, data);
  },

  setContact({ commit }, data) {
    commit(types.SET_CONTACT_RECORDS, [data]);
  },

  exportContacts: async ({ commit, dispatch }, params) => {
    try {
      const { data } = await ContactAPI.exportContacts(params);
      return data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error();
      }
      // Handle error
    }
  },

  async downloadContacts() {
    try {
      await ContactAPI.exportContacts();
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error();
      }
    }
  },

  getExportContactsStatus: async ({ commit }, params) => {
    const { data } = await ContactAPI.getExportContactsStatus(params);
    return data;
  },

  async statusOfLastDownloadContacts() {
    const { data } = await ContactAPI.getExportContactsStatus();
    return data;
  },

  async deleteLastDownloadContacts() {
    try {
      await ContactAPI.deleteExportContacts();
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error();
      }
    }
  },

  blockContact: async (
    { commit },
    { contactId, conversationId, period = null }
  ) => {
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      const response = await ContactAPI.block(
        contactId,
        conversationId,
        period
      );
      commit(types.UPDATE_CONTACTS_BLOCKED, response.data.payload);
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
      if (error.response?.data?.contact) {
        throw new DuplicateContactException(error.response.data.contact);
      } else {
        throw new Error(error);
      }
    }
  },

  unblockContact: async ({ commit }, { contactId, conversationId }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      const response = await ContactAPI.unblock(contactId, conversationId);
      commit(types.UPDATE_CONTACTS_BLOCKED, response.data.payload);
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
      if (error.response?.data?.contact) {
        throw new DuplicateContactException(error.response.data.contact);
      } else {
        throw new Error(error);
      }
    }
  },

  merge: async ({ commit }, { childId, parentId }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isMerging: true });
    try {
      const response = await AccountActionsAPI.merge(parentId, childId);
      commit(types.SET_CONTACT_RECORDS, [response.data]);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CONTACT_UI_FLAG, { isMerging: false });
    }
  },

  deleteContactThroughConversations: ({ commit }, id) => {
    commit(types.DELETE_CONTACT, id);
    commit(types.CLEAR_CONTACT_CONVERSATIONS, id, { root: true });
    commit(`contactConversations/${types.DELETE_CONTACT_CONVERSATION}`, id, {
      root: true,
    });
  },

  updateContact: async ({ commit }, updateObj) => {
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      commit(types.EDIT_CONTACT, updateObj);
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    }
  },

  filter: async (
    { commit },
    { page = 1, sortAttr, queryPayload, resetState = true, perPage } = {}
  ) => {
    const requestId = resetState ? startListRequest(commit) : null;
    try {
      const { data } = await ContactAPI.filter(
        page,
        sortAttr,
        queryPayload,
        perPage
      );
      if (resetState) commitContactsPage(commit, requestId, data);
      return data.payload;
    } catch (error) {
      if (resetState) failContactsPage(commit, requestId);
    }
    return [];
  },

  setContactFilters({ commit }, data) {
    commit(types.SET_CONTACT_FILTERS, data);
  },

  clearContactFilters({ commit }) {
    commit(types.CLEAR_CONTACT_FILTERS);
  },
};
